import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import styles from './style/Settings.module.css';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { sendToSelectedSerialPort } from './services/deviceSerial';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const MIN_CYCLE_HOURS = 4;
const MAX_CYCLE_HOURS = 24;

const MIN_VERIFY_MINUTES = 15;
const MAX_VERIFY_MINUTES = 240;

const MIN_VERIFY_THRESHOLD = 10;
const MAX_VERIFY_THRESHOLD = 200;

// Orario locale RTC, espresso in secondi dalla mezzanotte.
const clockToSeconds = (clock: string): number | null => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(clock);
  if (!match) return null;
  return Number(match[1]) * 3600 + Number(match[2]) * 60;
};

const LocationSelector = ({ setCoordinates }: any) => {
  useMapEvents({
    click(e) {
      setCoordinates({
        latitude: e.latlng.lat.toFixed(6),
        longitude: e.latlng.lng.toFixed(6),
      });
    },
  });

  return null;
};

const Settings: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const tenant = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('tenant')?.trim() || '';
  }, [location.search]);

  const [showForm, setShowForm] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [log, setLog] = useState<string>('');
  const [sending, setSending] = useState(false);
  // La diagnostica AT richiede un comando esplicito nel firmware STM32.
  // Non inviare comandi sconosciuti attraverso il protocollo CONFIG/INFO/CERT.
  const [diagnosticRequested, setDiagnosticRequested] = useState(false);
  const [provisioningStage, setProvisioningStage] = useState<
    'idle' | 'creating' | 'serial' | 'completed' | 'error'
  >('idle');

  const selectedSerialPortRef = useRef<any>(null);

  const [formData, setFormData] = useState({
    password: '',
    latitude: 0,
    longitude: 0,
    battery_status: '',
    battery_limit: '',
    level: '',
    level_limit: '',
    verifica_scarto: '',
    tempo_verifica_day_min: '',
    tempo_verifica_night_min: '',
    night_start: '',
    night_end: '',
    apn: '',
    description: '',
  });

  const [hours, setHours] = useState(MIN_CYCLE_HOURS);
  const [minutes, setMinutes] = useState(0);

  const [publicIp, setPublicIp] = useState<string>('');

  const [metricFile, setMetricFile] = useState<File | null>(null);
  const [metricFileError, setMetricFileError] = useState<string>('');

  useEffect(() => {
    const fetchPublicIp = async () => {
      if (!tenant) {
        setPublicIp('');
        setLog((prev) =>
          prev
            ? `${prev}\nTenant not found in the URL.`
            : 'Tenant not found in the URL.'
        );
        return;
      }

      try {
        const res = await axios.get('/api/get-ip', {
          headers: {
            'X-Tenant-Key': tenant,
          },
          withCredentials: true,
        });

        const ip = String(res.data?.ip || '').trim();

        if (!ip) {
          throw new Error('IP pubblico non presente nella risposta');
        }

        setPublicIp(ip);
        setLog((prev) =>
          prev
            ? `${prev}\nDetected Public IP: ${ip}`
            : `Detected Public IP: ${ip}`
        );
      } catch (err) {
        console.error('Errore recupero IP pubblico:', err);
        setPublicIp('');
        setLog((prev) =>
          prev
            ? `${prev}\nImpossibile recuperare l'IP pubblico dal backend.`
            : `Impossibile recuperare l'IP pubblico dal backend.`
        );
      }
    };

    fetchPublicIp();
  }, [tenant]);

  const setCoordinates = ({ latitude, longitude }: { latitude: string; longitude: string }) => {
    setFormData((prev) => ({
      ...prev,
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
    }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSerialConnect = async () => {
    try {
      if (!tenant) {
        setLog((prev) =>
          prev
            ? `${prev}\nTenant not found in the URL.`
            : 'Tenant not found in the URL.'
        );
        return;
      }

      if (!('serial' in navigator)) {
        setLog((prev) =>
          prev
            ? `${prev}\nWeb Serial API is not supported by this browser.`
            : 'Web Serial API is not supported by this browser.'
        );
        return;
      }

      const serialPort = await (navigator as any).serial.requestPort();
      selectedSerialPortRef.current = serialPort;

      setIsConnected(true);
      setShowForm(true);
      setLog((prev) =>
        prev
          ? `${prev}\nSerial port selected successfully.`
          : 'Serial port selected successfully.'
      );
    } catch (err) {
      console.error(err);
      setLog((prev) =>
        prev
          ? `${prev}\nError while selecting the serial port.`
          : 'Error while selecting the serial port.'
      );
    }
  };

  const handleMetricFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    setMetricFileError('');
    setMetricFile(null);

    if (!file) return;

    const allowed = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/json',
    ];

    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExt = ['csv'];

    if (!allowed.includes(file.type) && !allowedExt.includes(ext || '')) {
      setMetricFileError('Unsupported file format. Please upload a CSV file.');
      return;
    }

    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      setMetricFileError('File is too large (maximum 5 MB).');
      return;
    }

    setMetricFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tenant) {
      alert('Tenant not found in the URL.');
      return;
    }

    if (!publicIp) {
      alert("Public IP is not available. Please check the /api/get-ip endpoint.");
      return;
    }

    if (!selectedSerialPortRef.current) {
      alert('Please select a serial port first.');
      return;
    }

    const normalizedHours = Math.max(0, Math.min(MAX_CYCLE_HOURS, hours));
    const normalizedMinutes = Math.max(0, Math.min(59, minutes));
    const cycleInSeconds = normalizedHours * 3600 + normalizedMinutes * 60;
    const cycleInHours = cycleInSeconds / 3600;

    const tempoVerificaDayMinutes = Number(formData.tempo_verifica_day_min);
    const tempoVerificaNightMinutes = Number(formData.tempo_verifica_night_min);
    const tempoVerificaDaySeconds = tempoVerificaDayMinutes * 60;
    const tempoVerificaNightSeconds = tempoVerificaNightMinutes * 60;

    const nightStartSeconds = clockToSeconds(formData.night_start);
    const nightEndSeconds = clockToSeconds(formData.night_end);

    const verificaScarto =
      parseInt(String(formData.verifica_scarto ?? '0') || '0', 10);

    if (cycleInHours < MIN_CYCLE_HOURS || cycleInHours > MAX_CYCLE_HOURS) {
      alert(`Il ciclo deve essere compreso tra ${MIN_CYCLE_HOURS} e ${MAX_CYCLE_HOURS} ore.`);
      return;
    }

    if (
      !Number.isInteger(tempoVerificaDayMinutes) ||
      tempoVerificaDayMinutes < MIN_VERIFY_MINUTES ||
      tempoVerificaDayMinutes > MAX_VERIFY_MINUTES ||
      !Number.isInteger(tempoVerificaNightMinutes) ||
      tempoVerificaNightMinutes < MIN_VERIFY_MINUTES ||
      tempoVerificaNightMinutes > MAX_VERIFY_MINUTES
    ) {
      alert(
        `Entrambi i tempi di verifica devono essere compresi tra ${MIN_VERIFY_MINUTES} e ${MAX_VERIFY_MINUTES} minuti.`
      );
      return;
    }

    if (
      verificaScarto < MIN_VERIFY_THRESHOLD ||
      verificaScarto > MAX_VERIFY_THRESHOLD
    ) {
      alert(
        `Lo scarto verifica deve essere compreso tra ${MIN_VERIFY_THRESHOLD} e ${MAX_VERIFY_THRESHOLD}.`
      );
      return;
    }

    if (nightStartSeconds === null || nightEndSeconds === null || nightStartSeconds === nightEndSeconds) {
      alert('Seleziona un orario di inizio e uno di fine fascia notturna, validi e differenti.');
      return;
    }

    setSending(true);
    setProvisioningStage('creating');

    let newDeviceId: number | null = null;

    const { night_start, night_end, tempo_verifica_day_min, tempo_verifica_night_min, verifica_scarto, ...deviceFields } = formData;

    const payload = {
      ...deviceFields,
      cycle: cycleInSeconds,
      verify_time: tempoVerificaDaySeconds, // compatibilità con schema precedente
      verify_time_day: tempoVerificaDaySeconds,
      verify_time_night: tempoVerificaNightSeconds,
      night_start_sec: nightStartSeconds,
      night_end_sec: nightEndSeconds,
      verify_theshold: verificaScarto,
    };

    try {
      // Recupera automaticamente la CA pubblica usata dal Telit per verificare
      // il server HTTPS. NON e' la CA privata che firma i certificati client.
      // Fallisce prima della creazione del dispositivo se il backend non e' pronto.
      const caResponse = await axios.get('/api/device-server-ca', {
        headers: { 'X-Tenant-Key': tenant },
        withCredentials: true,
        responseType: 'text',
      });
      const certText = String(caResponse.data || '').replace(/\r\n?/g, '\n').trim();
      const pemBlocks = certText.match(/-----BEGIN CERTIFICATE-----[\s\S]*?-----END CERTIFICATE-----/g);
      if (!pemBlocks || pemBlocks.join('\n').trim() !== certText || certText.length > 64 * 1024 || certText.includes('#')) {
        throw new Error('La CA pubblica restituita dal backend non e un PEM valido.');
      }
      const certPlain = certText.replace(/\n/g, '\\n');
      setLog((prev) => `${prev}\nCA server recuperata automaticamente dal backend.`);

      const response = await axios.post('/api/devices', payload, {
        headers: {
          'X-Tenant-Key': tenant,
        },
        withCredentials: true,
      });

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Error while adding the device.");
      }

      newDeviceId = response.data?.id_device;

      if (!newDeviceId) {
        throw new Error('Device ID was not received from the backend.');
      }

      const soapPath =
        `/soap/uploadData?tenant=${encodeURIComponent(tenant)}`;

      const configMsg =
        `$CONFIG#${newDeviceId}` +
        `#${formData.password}` +
        `#${cycleInSeconds}` +
        `#${formData.apn}` +
        `#leonardo.e2net.it` +
        `#${soapPath}#\n`;

      const infoMsg =
        `$INFO#${tempoVerificaDaySeconds}` +
        `#${tempoVerificaNightSeconds}` +
        `#${verificaScarto}` +
        `#${nightStartSeconds}` +
        `#${nightEndSeconds}#\n`;

      const certMsg =
        `$CERT#${newDeviceId}` +
        `#${certPlain}#\n`;

      setLog((prev) =>
        prev
          ? `${prev}\nDevice creato con ID ${newDeviceId}. Invio seriale in corso [tenant=${tenant}]: CONFIG -> INFO -> CERT`
          : `Device creato con ID ${newDeviceId}. Invio seriale in corso [tenant=${tenant}]: CONFIG -> INFO -> CERT`
      );

      setProvisioningStage('serial');
      const serialResponse = await sendToSelectedSerialPort(
        selectedSerialPortRef.current,
        configMsg,
        infoMsg,
        certMsg
      );

      if (!serialResponse?.success) {
        throw new Error('Serial configuration was not completed.');
      }

      if (metricFile) {
        const form = new FormData();
        form.append('file', metricFile);

        await axios.post(`/api/devices/${newDeviceId}/metric-table`, form, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'X-Tenant-Key': tenant,
          },
          withCredentials: true,
        });
      }

      setLog((prev) =>
        prev
          ? `${prev}\nSeriale OK: ${JSON.stringify(serialResponse)}`
          : `Seriale OK: ${JSON.stringify(serialResponse)}`
      );

      setProvisioningStage('completed');
      if (diagnosticRequested) {
        setLog((prev) => `${prev}\nDiagnostica Telit richiesta: in attesa del supporto nel firmware STM32. Nessun comando AT aggiuntivo è stato inviato.`);
      }
      alert(`Device aggiunto e configurato con ID: ${newDeviceId} nel tenant ${tenant}`);
    } catch (error: any) {
      setProvisioningStage('error');
      console.error(error);

      if (newDeviceId) {
        try {
          await axios.delete(`/api/devices/${newDeviceId}`, {
            headers: {
              'X-Tenant-Key': tenant,
            },
            withCredentials: true,
          });

          setLog((prev) =>
            prev
              ? `${prev}\nErrore configurazione. Device ${newDeviceId} eliminato dal DB.`
              : `Errore configurazione. Device ${newDeviceId} eliminato dal DB.`
          );
        } catch (deleteError) {
          console.error('Errore rollback delete device:', deleteError);

          setLog((prev) =>
            prev
              ? `${prev}\nErrore configurazione. Impossibile eliminare il device ${newDeviceId}.`
              : `Errore configurazione. Impossibile eliminare il device ${newDeviceId}.`
          );
        }
      }

      const errorMessage =
      error?.response?.data?.error ||
      error?.message ||
      "Error while adding/configuring the device.";

      const lowerMessage = String(errorMessage).toLowerCase();

      const isTimeout =
        lowerMessage.includes("timeout") ||
        lowerMessage.includes("time out");

      if (isTimeout) {
        const goHome = window.confirm(
          `${errorMessage}

      The device configuration was not completed.

      Press OK to return to the Home page.
      Press Cancel to stay on the current page.`
        );

        if (goHome) {
          navigate("/");
        }
      } else {
        alert(errorMessage);
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.container}>
      <h1>Truck Configuration</h1>

      {!isConnected && (
        <button className={styles.addBtn} onClick={handleSerialConnect}>
          Select Serial Port
        </button>
      )}

      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label>Password</label>
            <input
              type="text"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={sending}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>APN</label>
            <input
              type="text"
              name="apn"
              value={formData.apn}
              onChange={handleChange}
              required
              disabled={sending}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Battery Threshold</label>
            <input
              type="number"
              name="battery_limit"
              value={formData.battery_limit}
              onChange={handleChange}
              required
              disabled={sending}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Level Threshold</label>
            <input
              type="number"
              name="level_limit"
              value={formData.level_limit}
              onChange={handleChange}
              required
              disabled={sending}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Cycle Hours (minimo {MIN_CYCLE_HOURS}, massimo {MAX_CYCLE_HOURS})</label>
            <input
              type="number"
              min={MIN_CYCLE_HOURS}
              max={MAX_CYCLE_HOURS}
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value, 10) || 0)}
              disabled={sending}
              required
            />
          </div>

          <div className={styles.inputGroup}>
            <label>Cycle Minutes</label>
            <input
              type="number"
              min="0"
              max="59"
              value={minutes}
              onChange={(e) => setMinutes(parseInt(e.target.value, 10) || 0)}
              disabled={sending}
              required
            />
            <p>
              Total Hours: <strong>{(hours + minutes / 60).toFixed(2)}</strong>
            </p>
            <p>
              Total Seconds: <strong>{hours * 3600 + minutes * 60}</strong>
            </p>
          </div>

          <div className={styles.inputGroup}>
            <label>Certificato CA server HTTPS</label>
            <small>Recuperato automaticamente dal backend durante la configurazione. Nessun file da selezionare.</small>
          </div>

          <div className={styles.inputGroup}>
            <label>Calibration Table (CSV)</label>
            <input
              type="file"
              accept=".csv,.xls,.xlsx,.json"
              onChange={handleMetricFileChange}
              disabled={sending}
            />
            {metricFile && (
              <small>
                Selected: <strong>{metricFile.name}</strong> ({Math.round(metricFile.size / 1024)} KB)
              </small>
            )}
            {metricFileError && <small style={{ color: 'red' }}>{metricFileError}</small>}
          </div>

          <div className={styles.inputGroup}>
            <label>Day Verification Interval (fuori dalla fascia notturna, minuti: {MIN_VERIFY_MINUTES}–{MAX_VERIFY_MINUTES})</label>
            <input
              type="number"
              min={MIN_VERIFY_MINUTES}
              max={MAX_VERIFY_MINUTES}
              step="1"
              name="tempo_verifica_day_min"
              value={formData.tempo_verifica_day_min}
              onChange={handleChange}
              required
              disabled={sending}
            />
            <p>Total Seconds: <strong>{Number(formData.tempo_verifica_day_min || 0) * 60}</strong></p>
          </div>

          <div className={styles.inputGroup}>
            <label>Night Verification Interval (fascia notturna configurabile, minuti: {MIN_VERIFY_MINUTES}–{MAX_VERIFY_MINUTES})</label>
            <input
              type="number"
              min={MIN_VERIFY_MINUTES}
              max={MAX_VERIFY_MINUTES}
              step="1"
              name="tempo_verifica_night_min"
              value={formData.tempo_verifica_night_min}
              onChange={handleChange}
              required
              disabled={sending}
            />
            <p>Total Seconds: <strong>{Number(formData.tempo_verifica_night_min || 0) * 60}</strong></p>
          </div>

          <div className={styles.inputGroup}>
            <label>Inizio fascia notturna (scegli l'orario)</label>
            <input type="time" name="night_start" step="60" value={formData.night_start}
              onChange={handleChange} required disabled={sending} />
          </div>

          <div className={styles.inputGroup}>
            <label>Fine fascia notturna (scegli l'orario)</label>
            <input type="time" name="night_end" step="60" value={formData.night_end}
              onChange={handleChange} required disabled={sending} />
          </div>

          <div className={styles.inputGroup}>
            <label>
              Level Verification Threshold (minimo {MIN_VERIFY_THRESHOLD}, massimo {MAX_VERIFY_THRESHOLD})
            </label>
            <input
              type="number"
              min={MIN_VERIFY_THRESHOLD}
              max={MAX_VERIFY_THRESHOLD}
              name="verifica_scarto"
              value={formData.verifica_scarto}
              onChange={handleChange}
              required
              disabled={sending}
            />
          </div>

          <label>Select a point on the map:</label>
          <MapContainer
            center={[41.9028, 12.4964]}
            zoom={13}
            scrollWheelZoom
            style={{ height: '400px', width: '100%', marginBottom: '1rem' }}
          >
            <TileLayer
              attribution="&copy; OpenStreetMap"
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationSelector setCoordinates={setCoordinates} />
            {formData.latitude !== 0 && formData.longitude !== 0 && (
              <Marker position={[+formData.latitude, +formData.longitude]} />
            )}
          </MapContainer>

          <div className={styles.inputGroup}>
            <label>Latitude</label>
            <input type="text" value={formData.latitude} readOnly />
          </div>

          <div className={styles.inputGroup}>
            <label>Longitude</label>
            <input type="text" value={formData.longitude} readOnly />
          </div>

          <div className={styles.inputGroup}>
            <label>Description</label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              disabled={sending}
            />
          </div>

          <div className={styles.inputGroup}>
            <label>
              <input
                type="checkbox"
                checked={diagnosticRequested}
                onChange={(e) => setDiagnosticRequested(e.target.checked)}
                disabled={sending}
              />
              {' '}Prepara la diagnostica Telit (modello, firmware e funzioni TLS)
            </label>
            <small>
              Questa opzione registra la richiesta nel log. I comandi AT non vengono
              ancora eseguiti: occorre prima aggiungere il comando diagnostico al
              firmware STM32, dopo CONFIG, INFO e CERT.
            </small>
            <p>Stato configurazione: <strong>{
              provisioningStage === 'idle' ? 'In attesa' :
              provisioningStage === 'creating' ? 'Creazione dispositivo nel backend' :
              provisioningStage === 'serial' ? 'Trasferimento CONFIG / INFO / CERT' :
              provisioningStage === 'completed' ? 'Configurazione completata' :
              'Errore di configurazione'
            }</strong></p>
          </div>

          <button type="submit" disabled={sending}>
            {sending ? 'Sending...' : 'Send'}
          </button>
        </form>
      )}
    </div>
  );
};

export default Settings;